/**
 * Links injuries to shot types for cross-referencing in Shot Analysis.
 * Injury keyword → shot keywords that commonly cause/relate to that injury.
 */
import { SportShot } from '../content';

// Injury keyword (in injury name) → shot keywords (in shot name) that relate
const INJURY_TO_SHOT_KEYWORDS: Record<string, string[]> = {
  elbow: ['forehand', 'backhand', 'dink', 'groundstroke', 'drive', 'volley', 'return', 'clear'],
  shoulder: ['serve', 'overhead', 'smash', 'volley', 'clear', 'drop', 'lob'],
  knee: ['lunge', 'dink', 'third shot', 'split', 'movement', 'ready'],
  back: ['dink', 'ready', 'bend', 'low', 'twist', 'lunge'],
  wrist: ['dink', 'volley', 'flick', 'drop', 'net'],
  ankle: ['lateral', 'movement', 'shuffle', 'split', 'direction'],
  achilles: ['jump', 'overhead', 'run', 'lunge', 'movement'],
  plantar: ['court', 'movement', 'impact', 'run'],
  'rotator cuff': ['serve', 'overhead', 'smash', 'volley'],
  impingement: ['serve', 'overhead', 'smash'],
  tendinitis: ['repetitive', 'serve', 'forehand', 'backhand', 'dink'],
  overview: [], // Injuries Overview - no specific shot link
};

// Shot keyword → injury keywords that this shot can cause
const SHOT_TO_INJURY_KEYWORDS: Record<string, string[]> = {
  serve: ['shoulder', 'elbow', 'back', 'rotator cuff', 'impingement'],
  overhead: ['shoulder', 'rotator cuff', 'impingement', 'achilles'],
  smash: ['shoulder', 'rotator cuff', 'impingement', 'achilles'],
  forehand: ['elbow', 'wrist', 'shoulder', 'back'],
  backhand: ['elbow', 'wrist', 'shoulder', 'back'],
  dink: ['elbow', 'wrist', 'knee', 'back'],
  volley: ['elbow', 'wrist', 'shoulder'],
  groundstroke: ['elbow', 'wrist', 'back'],
  drive: ['elbow', 'wrist'],
  lunge: ['knee', 'ankle', 'achilles', 'back'],
  'third shot': ['knee', 'elbow', 'shoulder'],
  return: ['elbow', 'wrist', 'shoulder'],
  clear: ['shoulder', 'elbow'],
  drop: ['shoulder', 'wrist', 'knee'],
  lob: ['shoulder', 'back'],
};

function getKeywordsFromName(name: string, keywordMap: Record<string, string[]>): string[] {
  const nameLower = name.toLowerCase();
  const matched: string[] = [];
  for (const [keyword, _] of Object.entries(keywordMap)) {
    if (nameLower.includes(keyword)) {
      matched.push(keyword);
    }
  }
  return matched;
}

/** Get injuries related to a shot type (e.g. "Forehand" → "Pickleball Elbow", "Wrist") */
export function getRelatedInjuries(
  shotName: string,
  injuryShots: SportShot[],
  _sportId?: string
): SportShot[] {
  const shotLower = shotName.toLowerCase();
  const injuryKeywords = new Set<string>();

  for (const [shotKw, injuries] of Object.entries(SHOT_TO_INJURY_KEYWORDS)) {
    if (shotLower.includes(shotKw)) {
      injuries.forEach(ik => injuryKeywords.add(ik));
    }
  }

  if (injuryKeywords.size === 0) return [];

  return injuryShots.filter((inj) => {
    if (inj.name === 'Injuries Overview') return false;
    const injLower = inj.name.toLowerCase();
    return [...injuryKeywords].some((kw) => injLower.includes(kw));
  });
}

/** Get shots related to an injury (e.g. "Pickleball Elbow" → "Forehand", "Dink") */
export function getRelatedShots(
  injuryName: string,
  allShots: SportShot[],
  injuryShots: SportShot[],
  _sportId?: string
): SportShot[] {
  const injuryKeywords = getKeywordsFromName(injuryName, INJURY_TO_SHOT_KEYWORDS);
  if (injuryKeywords.length === 0 || injuryName.toLowerCase().includes('overview')) return [];

  const shotKeywords = new Set<string>();
  injuryKeywords.forEach((ik) => {
    const shots = INJURY_TO_SHOT_KEYWORDS[ik];
    if (shots) shots.forEach((sk) => shotKeywords.add(sk));
  });

  const injuryNames = new Set(injuryShots.map((s) => s.name));
  return allShots.filter((shot) => {
    if (injuryNames.has(shot.name)) return false; // exclude injury "shots"
    const shotLower = shot.name.toLowerCase();
    return [...shotKeywords].some((kw) => shotLower.includes(kw));
  });
}
